import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Zap, Activity } from 'lucide-react';
import { aiRegistryClient, type Analytics } from '../../services/ai-registry-client';

export type { Analytics };

export function AnalyticsMain() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'hour' | 'day' | 'week' | 'month'>('day');

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await aiRegistryClient.getAnalytics({ period });
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatLatency = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const successRate = analytics
    ? ((analytics.successful / analytics.totalRuns) * 100).toFixed(1)
    : '0';

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-uw-interactive-primary" />
          <h1 className="text-3xl font-bold text-uw-text-primary">AI Analytics</h1>
        </div>
        <div>
          <label className="mr-2 text-sm font-medium text-uw-text-secondary">Period:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
          >
            <option value="hour">Last Hour</option>
            <option value="day">Last 24 Hours</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading analytics...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && analytics && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Runs */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-uw-text-primary">
                  {formatNumber(analytics.totalRuns)}
                </span>
              </div>
              <h3 className="text-sm font-medium text-uw-text-secondary">Total Runs</h3>
              <p className="text-xs text-uw-text-tertiary mt-1">
                {formatNumber(analytics.successful)} successful, {formatNumber(analytics.failed)}{' '}
                failed
              </p>
            </div>

            {/* Success Rate */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-uw-text-primary">{successRate}%</span>
              </div>
              <h3 className="text-sm font-medium text-uw-text-secondary">Success Rate</h3>
              <p className="text-xs text-uw-text-tertiary mt-1">
                {formatNumber(analytics.successful)} / {formatNumber(analytics.totalRuns)} runs
              </p>
            </div>

            {/* Total Cost */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-yellow-600" />
                <span className="text-2xl font-bold text-uw-text-primary">
                  {formatCurrency(analytics.totalCost)}
                </span>
              </div>
              <h3 className="text-sm font-medium text-uw-text-secondary">Total Cost</h3>
              <p className="text-xs text-uw-text-tertiary mt-1">
                {formatNumber(analytics.totalTokens)} total tokens
              </p>
            </div>

            {/* Average Latency */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-uw-text-primary">
                  {formatLatency(analytics.avgLatency)}
                </span>
              </div>
              <h3 className="text-sm font-medium text-uw-text-secondary">Avg Latency</h3>
              <p className="text-xs text-uw-text-tertiary mt-1">Mean response time</p>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Breakdown */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <h3 className="text-lg font-semibold text-uw-text-primary mb-4">Cost Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Average per run:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {formatCurrency(
                      analytics.totalRuns > 0 ? analytics.totalCost / analytics.totalRuns : 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Estimated monthly:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {formatCurrency(analytics.totalCost * 30)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Cost per 1M tokens:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {formatCurrency(
                      analytics.totalTokens > 0
                        ? (analytics.totalCost / analytics.totalTokens) * 1_000_000
                        : 0
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <h3 className="text-lg font-semibold text-uw-text-primary mb-4">
                Performance Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Error rate:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {analytics.totalRuns > 0
                      ? ((analytics.failed / analytics.totalRuns) * 100).toFixed(1)
                      : '0'}
                    %
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Avg tokens per run:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {formatNumber(
                      analytics.totalRuns > 0
                        ? Math.round(analytics.totalTokens / analytics.totalRuns)
                        : 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-uw-text-secondary">Runs per hour:</span>
                  <span className="text-sm font-medium text-uw-text-primary">
                    {formatNumber(
                      period === 'hour'
                        ? analytics.totalRuns
                        : period === 'day'
                        ? Math.round(analytics.totalRuns / 24)
                        : period === 'week'
                        ? Math.round(analytics.totalRuns / (24 * 7))
                        : Math.round(analytics.totalRuns / (24 * 30))
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && !error && !analytics && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-12 text-center">
          <BarChart3 className="w-16 h-16 text-uw-text-tertiary mx-auto mb-4" />
          <p className="text-uw-text-secondary">No analytics data available for this period</p>
        </div>
      )}
    </div>
  );
}
